import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Shield, ArrowRight, Search } from "lucide-react";
import { useState } from "react";
import { vendorType } from "@/constants/vendorType";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { setFilters } from "@/redux/vendor.search.slice";
import useLocationAutocomplete from "@/hooks/useLocationAutocomplete";
import { Paper } from "@mui/material";

export function HeroSection() {
  const [selectedService, setSelectedService] = useState("");
  const [location, setLocation] = useState("");
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {
    query,
    suggestions,
    loading,
    error,
    handleInputChange,
    handleSelectPlace,
    handleClear,
    placeDetails,
    placeLoading,
    setQuery,
  } = useLocationAutocomplete({
    onLocationSelect: (location) => {
      console.log("Selected location:", location);
    }
  });

  const handleSearch = () => {
    localStorage.setItem("homePage", "false");
    dispatch(
      setFilters({
        vendorType: selectedService ? [selectedService] : [],
        rating: null,
        serviceType: [],
      })
    );
    navigate("/customer/search-brokers");
  };

  return (
    <section
      id="home"
      className="pt-16 min-h-screen flex items-center px-4 sm:px-6"
      style={{
        background: "linear-gradient(135deg, rgb(106, 0, 244), rgb(111, 109, 248))"
      }}
    >
      <div className="max-w-7xl mx-auto w-full py-16 sm:py-20">
        <div className="text-center">

          {/* Main Heading */}
          <motion.h1
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-extrabold text-white mb-6 sm:mb-8 leading-snug sm:leading-tight"
            style={{ fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif' }}
          >
            <span className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl">
              Find
            </span>{" "}
            Trusted Home Buying Experts
            <br />
            <motion.span
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl"
              style={{ color: "#f1c40f" }}
            >
              All in One Place
            </motion.span>
          </motion.h1>

          {/* Trust Indicators */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="flex justify-center mb-8 sm:mb-12 px-4"
          >
            <div className="glass-bg rounded-xl px-4 sm:px-8 py-3 sm:py-4 max-w-xl text-sm sm:text-base">
              <div className="flex items-center justify-center text-white text-center">
                <Shield className="text-green-400 h-5 w-5 sm:h-6 sm:w-6 mr-2 sm:mr-3" />
                <span className="font-medium">Professionals are vetted for licensing and experience</span>
              </div>
            </div>
          </motion.div>

          {/* Search Form */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="max-w-4xl mx-auto mb-10 sm:mb-12"
          >
            <div className="glass-bg rounded-2xl p-6 sm:p-8">
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                {/* Service Selector */}
                <Select value={selectedService} onValueChange={(value) => setSelectedService(value === "none" ? "" : value)}>
                  <SelectTrigger className="bg-white/20 border-white/30 text-white placeholder:text-white h-12 sm:h-14 text-base sm:text-lg pr-10">
                    <SelectValue placeholder="I am looking for..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">All</SelectItem>
                    {vendorType.map((service) => (
                      <SelectItem key={service} value={service}>
                        {service}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {/* Location Input with Suggestions */}
                <div className="relative">
                  <Input
                    type="text"
                    placeholder="Enter your suburb or postcode"
                    value={query}
                    onChange={handleInputChange}
                    className="bg-white/20 border-white/30 text-white placeholder:text-white/70 h-12 sm:h-14 text-base sm:text-lg pr-10"
                  />
                  {query && (
                    <button
                      onClick={handleClear}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-white text-sm"
                    >
                      ✖
                    </button>
                  )}

                  {suggestions.length > 0 && !placeDetails && (
                    <Paper
                      elevation={3}
                      className="absolute w-full mt-2 max-h-60 overflow-y-auto bg-white rounded-lg shadow-lg z-50 text-left"
                    >
                      {suggestions.map((place, index) => (
                        <div
                          key={index}
                          onClick={() => handleSelectPlace(place)}
                          className="p-2 cursor-pointer hover:bg-gray-100"
                        >
                          {place}
                        </div>
                      ))}
                    </Paper>
                  )}
                  {error && <p className="text-red-500 text-sm mt-2">{error}</p>}
                  {loading && <p className="text-gray-300 text-sm mt-2">Loading...</p>}
                </div>

                {/* Search Button */}
                <Button
                  onClick={handleSearch}
                  size="lg"
                  className="bg-white text-purple px-6 sm:px-8 h-12 sm:h-14 text-base sm:text-lg font-semibold hover:bg-yellow-100 transition-all duration-300 transform hover:scale-105 shadow-lg"
                >
                  <Search className="mr-2 h-5 w-5" />
                  Search
                </Button>
              </div>
            </div>
          </motion.div>

          {/* Value Propositions */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.8 }}
            className="space-y-2 sm:space-y-4"
          >
            <p className="text-base sm:text-lg text-white/90 font-medium">
              Free to use. No obligation. Takes under a minute
            </p>
            <p className="text-sm sm:text-base text-yellow-200 font-medium">
              We're building with transparency and trust from day one
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
